import React, { useState, useEffect, useRef } from 'react';
import { Card, Descriptions, Tag, Typography, Row, Col, Table, Space, Spin, Collapse, Alert } from 'antd';
import { UpdateOperation, EntityType } from './types';
import { PlusOutlined, MinusOutlined, EditOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { Product, Entitlement, SKU } from '../../types';

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface ReviewChangesProps {
  mainUpdates: UpdateOperation[];
  relatedUpdates: UpdateOperation[];
  entityType: EntityType;
  entityData: Record<string, unknown> | null;
}

const ReviewChanges: React.FC<ReviewChangesProps> = ({
  mainUpdates,
  relatedUpdates,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [skus, setSKUs] = useState<SKU[]>([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  useEffect(() => {
    const loadData = async () => {
      if (loadingRef.current) {
        return; // Prevent duplicate calls
      }

      loadingRef.current = true;
      try {
        const [productsData, entitlementsData, skusData] = await Promise.all([
          apiService.getProducts(),
          apiService.getEntitlements(),
          apiService.getSKUs(),
        ]);
        setProducts(productsData || []);
        setEntitlements(entitlementsData || []);
        setSKUs(skusData || []);
      } catch {
        // Failed to load data for review
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };
    loadData();
  }, []);

  const formatValue = (val: unknown): string => {
    if (val === null || val === undefined) {
      return 'N/A';
    }
    if (typeof val === 'boolean') {
      return val ? 'Yes' : 'No';
    }
    return String(val);
  };

  const renderChange = (key: string, oldValue: unknown, newValue: unknown) => {
    const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    return (
      <Descriptions.Item label={label} key={key}>
        <Row gutter={16}>
          <Col span={12}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>Old Value</Text>
              <div>
                <Text delete type="danger">{formatValue(oldValue)}</Text>
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>New Value</Text>
              <div>
                <Text strong style={{ color: '#52c41a' }}>{formatValue(newValue)}</Text>
              </div>
            </div>
          </Col>
        </Row>
      </Descriptions.Item>
    );
  };

  const renderUpdateSection = (update: UpdateOperation) => {
    // Check if this is a DEPENDENT_SERVICE update (SKU associations)
    if (
      update.entityType === EntityType.DEPENDENT_SERVICE &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      ('added_sku_ids' in update.updates || 'removed_sku_ids' in update.updates)
    ) {
      return renderDependentServiceUpdateSection(update);
    }

    // Check if this is an entitlement update from EntitlementTableEditor
    if (
      update.entityType === EntityType.ENTITLEMENT &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      ('entitlements' in update.updates || 'orgEntitlements' in update.updates)
    ) {
      return renderEntitlementUpdateSection(update);
    }

    // Check if this is a SKU config update with structured operations
    if (
      (update.entityType === EntityType.SKU_STANDALONE ||
        update.entityType === EntityType.SKU_SUITE ||
        update.entityType === EntityType.SKU_ADDON) &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      'deleteOperations' in update.updates &&
      'addOperations' in update.updates
    ) {
      return renderSKUConfigUpdateSection(update);
    }

    // Check if this is a Regions & Questions update
    if (
      update.entityType === EntityType.PRODUCT &&
      typeof update.updates === 'object' &&
      update.updates !== null &&
      'regionsQuestions' in update.updates
    ) {
      return renderRegionsQuestionsUpdateSection(update);
    }

    // Regular entity update
    return (
      <Card
        key={update.entityId}
        style={{ marginBottom: 16 }}
        title={
          <div>
            <Tag color="blue">{update.entityType.replace('_', ' ').toUpperCase()}</Tag>
            <Text strong>{update.entityName}</Text>
          </div>
        }
      >
        <Descriptions bordered column={1}>
          {Object.entries(update.updates).map(([key, newValue]) => {
            const oldValue = update.originalData[key];
            return renderChange(key, oldValue, newValue);
          })}
        </Descriptions>
      </Card>
    );
  };

  const renderDependentServiceUpdateSection = (update: UpdateOperation) => {
    const updates = update.updates as Record<string, unknown>;
    const addedSKUIDs = (updates.added_sku_ids || []) as number[];
    const removedSKUIDs = (updates.removed_sku_ids || []) as number[];

    const getSKUName = (skuId: number) => {
      const sku = skus.find(s => s.id === skuId);
      return sku ? sku.name : `Unknown SKU`;
    };

    return (
      <Card
        key={update.entityId}
        style={{ marginBottom: 16 }}
        title={
          <div>
            <Tag color="purple">DEPENDENT SERVICE</Tag>
            <Text strong>{update.entityName}</Text>
          </div>
        }
      >
        {addedSKUIDs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={`${addedSKUIDs.length} SKU${addedSKUIDs.length > 1 ? 's' : ''} to be Added`}
              type="success"
              showIcon
              icon={<PlusOutlined />}
              style={{ marginBottom: 12 }}
            />
            <Table
              dataSource={addedSKUIDs.map((skuId) => ({
                key: skuId,
                skuId,
                skuName: getSKUName(skuId),
              }))}
              columns={[
                {
                  title: 'SKU ID',
                  dataIndex: 'skuId',
                  key: 'skuId',
                  width: 100,
                  render: (id: number) => <Tag color="green">{id}</Tag>,
                },
                {
                  title: 'SKU Name',
                  dataIndex: 'skuName',
                  key: 'skuName',
                  render: (name: string) => <Text strong>{name}</Text>,
                },
              ]}
              pagination={false}
              size="small"
            />
          </div>
        )}

        {removedSKUIDs.length > 0 && (
          <div>
            <Alert
              message={`${removedSKUIDs.length} SKU${removedSKUIDs.length > 1 ? 's' : ''} to be Removed`}
              type="error"
              showIcon
              icon={<MinusOutlined />}
              style={{ marginBottom: 12 }}
            />
            <Table
              dataSource={removedSKUIDs.map((skuId) => ({
                key: skuId,
                skuId,
                skuName: getSKUName(skuId),
              }))}
              columns={[
                {
                  title: 'SKU ID',
                  dataIndex: 'skuId',
                  key: 'skuId',
                  width: 100,
                  render: (id: number) => <Tag color="red">{id}</Tag>,
                },
                {
                  title: 'SKU Name',
                  dataIndex: 'skuName',
                  key: 'skuName',
                  render: (name: string) => (
                    <Text delete type="secondary">{name}</Text>
                  ),
                },
              ]}
              pagination={false}
              size="small"
            />
          </div>
        )}

        {addedSKUIDs.length === 0 && removedSKUIDs.length === 0 && (
          <Text type="secondary">No SKU changes</Text>
        )}
      </Card>
    );
  };

  const renderSKUConfigUpdateSection = (update: UpdateOperation) => {
    const updates = update.updates as Record<string, unknown>;
    const deleteOps = (updates.deleteOperations || []) as Array<Record<string, unknown>>;
    const addOps = (updates.addOperations || []) as Array<Record<string, unknown>>;

    const getProductName = (productId: number) => {
      const product = products.find(p => p.id === productId);
      return product ? product.name : `Unknown Product (ID: ${productId})`;
    };

    const getEntitlementName = (entitlementId: number) => {
      const entitlement = entitlements.find(e => e.id === entitlementId);
      return entitlement ? `${entitlement.name}: ${entitlement.value}` : `Unknown Entitlement (ID: ${entitlementId})`;
    };

    const deleteColumns = [
      {
        title: 'Product',
        dataIndex: 'ProductID',
        key: 'ProductID',
        render: (productId: number) => (
          <div>
            <Text strong>{getProductName(productId)}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>ID: {productId}</Text>
          </div>
        ),
      },
      {
        title: 'Operation',
        key: 'operation',
        width: 100,
        render: () => <Text type="secondary">-</Text>,
      },
      {
        title: 'Operand',
        key: 'operand',
        width: 100,
        render: () => <Text type="secondary">-</Text>,
      },
      {
        title: 'Force Region',
        key: 'forceRegion',
        width: 120,
        render: () => <Text type="secondary">-</Text>,
      },
      {
        title: 'Entitlements',
        dataIndex: 'entitlementIds',
        key: 'entitlements',
        render: (entitlementIds: number[]) => (
          <Space direction="vertical" size="small">
            {entitlementIds && entitlementIds.length > 0 ? (
              entitlementIds.map((entId, idx) => (
                <div key={idx}>
                  <Tag color="red">{getEntitlementName(entId)}</Tag>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>ID: {entId}</Text>
                </div>
              ))
            ) : (
              <Text type="secondary">No entitlements</Text>
            )}
          </Space>
        ),
      },
    ];

    const addColumns = [
      {
        title: 'Product',
        dataIndex: ['config', 'ProductID'],
        key: 'ProductID',
        render: (productId: number) => (
          <div>
            <Text strong>{getProductName(productId)}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>ID: {productId}</Text>
          </div>
        ),
      },
      {
        title: 'Operation',
        dataIndex: ['config', 'Operation'],
        key: 'Operation',
        width: 120,
        render: (val: string) => val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">-</Text>,
      },
      {
        title: 'Operand',
        dataIndex: ['config', 'Operand'],
        key: 'Operand',
        width: 100,
        render: (val: number) => val !== undefined && val !== null ? <Tag>{val}</Tag> : <Text type="secondary">-</Text>,
      },
      {
        title: 'Force Region',
        dataIndex: ['config', 'ForceRegionName'],
        key: 'ForceRegionName',
        width: 120,
        render: (val: string) => val ? <Tag color="orange">{val}</Tag> : <Text type="secondary">-</Text>,
      },
      {
        title: 'Allowed Regions',
        dataIndex: ['config', 'AllowedRegions'],
        key: 'AllowedRegions',
        render: (val: string) => val
          ? <Space size={[4, 4]} wrap>{val.split(',').map((r) => <Tag key={r} color="green">{r.trim()}</Tag>)}</Space>
          : <Text type="secondary">-</Text>,
      },
      {
        title: 'Entitlements',
        dataIndex: 'entitlements',
        key: 'entitlements',
        render: (entitlements: Array<Record<string, unknown>>) => (
          <Space direction="vertical" size="small">
            {entitlements.map((ent, idx) => (
              <div key={idx}>
                <Tag color="green">{getEntitlementName(ent.EntitlementID as number)}</Tag>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>ID: {String(ent.EntitlementID)}</Text>
              </div>
            ))}
            {entitlements.length === 0 && <Text type="secondary">No entitlements</Text>}
          </Space>
        ),
      },
    ];

    return (
      <Card
        key={update.entityId}
        style={{ marginBottom: 16 }}
        title={
          <div>
            <Tag color="blue">{update.entityType.replace('_', ' ').toUpperCase()}</Tag>
            <Text strong>{update.entityName}</Text>
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>SKU ID: {update.entityId}</Text>
          </div>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Collapse defaultActiveKey={['summary']} size="small">
            <Panel
              header={
                <Space>
                  <Text strong>Configuration Changes Summary</Text>
                  <Tag color="red">{deleteOps.length} to remove</Tag>
                  <Tag color="green">{addOps.length} to add</Tag>
                </Space>
              }
              key="summary"
            >
              <Text type="secondary">
                The table below shows the old configuration (to be deleted) and new configuration (to be added).
                Both are displayed in the same table format for easy comparison.
              </Text>
            </Panel>
          </Collapse>

          {deleteOps.length > 0 && (
            <div>
              <Title level={5} style={{ color: '#cf1322' }}>
                <MinusOutlined /> Old Configuration (Will be Deleted)
              </Title>
              <Table
                dataSource={deleteOps}
                columns={deleteColumns}
                pagination={false}
                size="small"
                rowKey={(record: Record<string, unknown>) => `del-${record.ProductID}-${record.SkuID}`}
                bordered
                style={{ marginBottom: 24 }}
              />
            </div>
          )}

          {addOps.length > 0 && (
            <div>
              <Title level={5} style={{ color: '#389e0d' }}>
                <PlusOutlined /> New Configuration (Will be Added)
              </Title>
              <Table
                dataSource={addOps}
                columns={addColumns}
                pagination={false}
                size="small"
                rowKey={(record: Record<string, unknown>) => `add-${(record.config as Record<string, unknown>)?.ProductID}-${(record.config as Record<string, unknown>)?.SkuID}`}
                bordered
              />
            </div>
          )}

          {deleteOps.length === 0 && addOps.length === 0 && (
            <Card size="small">
              <Text type="secondary">No configuration changes</Text>
            </Card>
          )}
        </Space>
      </Card>
    );
  };

  const renderRegionsQuestionsUpdateSection = (update: UpdateOperation) => {
    const payload = (update.updates as Record<string, unknown>).regionsQuestions as {
      allowedRegions: string[];
      questions: Array<{
        id?: number;
        name: string;
        display_name: string;
        description: string;
        type: string;
        _status: 'existing' | 'new' | 'deleted';
      }>;
    };
    const original = (update.originalData as Record<string, unknown>).regionsQuestions as {
      allowedRegions: string[];
      questions: typeof payload.questions;
    };

    const origRegions = original?.allowedRegions ?? [];
    const newRegions = payload?.allowedRegions ?? [];
    const addedRegions = newRegions.filter((r) => !origRegions.includes(r));
    const removedRegions = origRegions.filter((r) => !newRegions.includes(r));
    const regionsChanged = addedRegions.length > 0 || removedRegions.length > 0;

    const newQuestions = payload?.questions?.filter((q) => q._status === 'new') ?? [];
    const deletedQuestions = payload?.questions?.filter((q) => q._status === 'deleted') ?? [];
    const updatedQuestions = payload?.questions?.filter((q) => {
      if (q._status !== 'existing') { return false; }
      const orig = original?.questions?.find((oq) => oq.id === q.id);
      return orig && (
        orig.name !== q.name ||
        orig.display_name !== q.display_name ||
        orig.description !== q.description ||
        orig.type !== q.type
      );
    }) ?? [];

    const hasChanges = regionsChanged || newQuestions.length > 0 || deletedQuestions.length > 0 || updatedQuestions.length > 0;

    return (
      <Card
        key={`rq-${update.entityId}`}
        style={{ marginBottom: 16 }}
        title={
          <div>
            <Tag color="cyan">REGIONS & QUESTIONS</Tag>
            <Text strong>{update.entityName}</Text>
          </div>
        }
      >
        {!hasChanges && (
          <Text type="secondary">No changes to regions or provisioning questions.</Text>
        )}

        {/* ── Allowed Regions ── */}
        {regionsChanged && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Allowed Regions</Title>
            <Row gutter={16}>
              {removedRegions.length > 0 && (
                <Col span={12}>
                  <Alert
                    message={`${removedRegions.length} region${removedRegions.length > 1 ? 's' : ''} removed`}
                    type="error"
                    showIcon
                    icon={<MinusOutlined />}
                    style={{ marginBottom: 8 }}
                  />
                  <Space wrap>
                    {removedRegions.map((r) => (
                      <Tag key={r} color="red" icon={<MinusOutlined />}>{r}</Tag>
                    ))}
                  </Space>
                </Col>
              )}
              {addedRegions.length > 0 && (
                <Col span={12}>
                  <Alert
                    message={`${addedRegions.length} region${addedRegions.length > 1 ? 's' : ''} added`}
                    type="success"
                    showIcon
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 8 }}
                  />
                  <Space wrap>
                    {addedRegions.map((r) => (
                      <Tag key={r} color="green" icon={<PlusOutlined />}>{r}</Tag>
                    ))}
                  </Space>
                </Col>
              )}
            </Row>
          </div>
        )}

        {/* ── New Questions ── */}
        {newQuestions.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ color: '#52c41a' }}>
              <PlusOutlined /> New Provisioning Questions ({newQuestions.length})
            </Title>
            <Table
              dataSource={newQuestions.map((q, i) => ({ ...q, key: `new-${i}` }))}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Display Name', dataIndex: 'display_name', key: 'display_name' },
                { title: 'Type', dataIndex: 'type', key: 'type', render: (t: string) => <Tag>{t}</Tag> },
                { title: 'Description', dataIndex: 'description', key: 'description' },
              ]}
              pagination={false}
              size="small"
              bordered
            />
          </div>
        )}

        {/* ── Updated Questions ── */}
        {updatedQuestions.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ color: '#faad14' }}>
              <EditOutlined /> Updated Provisioning Questions ({updatedQuestions.length})
            </Title>
            {updatedQuestions.map((q) => {
              const orig = original?.questions?.find((oq) => oq.id === q.id);
              return (
                <Card key={q.id} size="small" style={{ marginBottom: 8 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    ID {q.id} — {orig?.display_name || orig?.name}
                  </Text>
                  <Descriptions bordered column={2} size="small">
                    {orig?.name !== q.name && (
                      <Descriptions.Item label="Name" span={2}>
                        <Text delete type="danger">{orig?.name}</Text>
                        {' → '}
                        <Text strong style={{ color: '#52c41a' }}>{q.name}</Text>
                      </Descriptions.Item>
                    )}
                    {orig?.display_name !== q.display_name && (
                      <Descriptions.Item label="Display Name" span={2}>
                        <Text delete type="danger">{orig?.display_name}</Text>
                        {' → '}
                        <Text strong style={{ color: '#52c41a' }}>{q.display_name}</Text>
                      </Descriptions.Item>
                    )}
                    {orig?.type !== q.type && (
                      <Descriptions.Item label="Type" span={2}>
                        <Tag color="red">{orig?.type}</Tag>
                        {' → '}
                        <Tag color="green">{q.type}</Tag>
                      </Descriptions.Item>
                    )}
                    {orig?.description !== q.description && (
                      <Descriptions.Item label="Description" span={2}>
                        <Text delete type="danger">{orig?.description || '—'}</Text>
                        {' → '}
                        <Text strong style={{ color: '#52c41a' }}>{q.description || '—'}</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              );
            })}
          </div>
        )}

        {/* ── Deleted Questions ── */}
        {deletedQuestions.length > 0 && (
          <div>
            <Title level={5} style={{ color: '#cf1322' }}>
              <MinusOutlined /> Deleted Provisioning Questions ({deletedQuestions.length})
            </Title>
            <Table
              dataSource={deletedQuestions.map((q) => ({ ...q, key: `del-${q.id}` }))}
              columns={[
                { title: 'ID', dataIndex: 'id', key: 'id', width: 60, render: (id: number) => <Tag color="red">{id}</Tag> },
                { title: 'Name', dataIndex: 'name', key: 'name', render: (n: string) => <Text delete type="secondary">{n}</Text> },
                { title: 'Display Name', dataIndex: 'display_name', key: 'display_name', render: (n: string) => <Text delete type="secondary">{n}</Text> },
                { title: 'Type', dataIndex: 'type', key: 'type', render: (t: string) => <Tag color="red">{t}</Tag> },
              ]}
              pagination={false}
              size="small"
              bordered
            />
          </div>
        )}
      </Card>
    );
  };

  const renderEntitlementUpdateSection = (update: UpdateOperation) => {
    const changedEnts = ((update.updates as Record<string, unknown>).entitlements as Array<Record<string, unknown>>) || [];
    const changedOrgEnts = ((update.updates as Record<string, unknown>).orgEntitlements as Array<Record<string, unknown>>) || [];

    const insertedEnts = changedEnts.filter((e) => e.action === 'insert');
    const updatedEnts = changedEnts.filter((e) => e.action === 'update');
    const deletedEnts = changedEnts.filter((e) => e.action === 'delete');

    const insertedOrgEnts = changedOrgEnts.filter((e) => e.action === 'insert');
    const updatedOrgEnts = changedOrgEnts.filter((e) => e.action === 'update');
    const deletedOrgEnts = changedOrgEnts.filter((e) => e.action === 'delete');

    const totalInserted = insertedEnts.length + insertedOrgEnts.length;
    const totalUpdated = updatedEnts.length + updatedOrgEnts.length;
    const totalDeleted = deletedEnts.length + deletedOrgEnts.length;
    const totalChangesCount = totalInserted + totalUpdated + totalDeleted;

    if (totalChangesCount === 0) {
      return null;
    }

    // Find original values for updated rows to show old → new
    const origEnts = ((update.originalData as Record<string, unknown>).entitlements as Array<Record<string, unknown>>) || [];
    const origOrgEnts = ((update.originalData as Record<string, unknown>).orgEntitlements as Array<Record<string, unknown>>) || [];

    const stdColumns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 70, render: (val: unknown) => val ? <Text type="secondary">{String(val)}</Text> : <Text type="secondary">—</Text> },
      { title: 'Name', dataIndex: 'name', key: 'name', render: (val: string) => <Text strong>{val}</Text> },
      { title: 'Value', dataIndex: 'value', key: 'value' },
      { title: 'Description', dataIndex: 'description', key: 'description', render: (val: string) => val || <Text type="secondary">—</Text> },
      { title: 'Needs Qty', dataIndex: 'needs_quantity', key: 'needs_quantity', width: 100, render: (val: boolean) => val ? 'Yes' : 'No' },
      { title: 'Unit', dataIndex: 'unit', key: 'unit', render: (val: string) => val || <Text type="secondary">—</Text> },
    ];

    const orgColumns = [
      ...stdColumns,
      { title: 'Access Type', dataIndex: 'access_type', key: 'access_type', width: 110 },
    ];

    // For updated rows, build a comparison view showing old → new for changed fields
    const renderUpdatedTable = (rows: Array<Record<string, unknown>>, originals: Array<Record<string, unknown>>, fields: string[], _isOrg: boolean) => {
      if (rows.length === 0) { return null; }

      const comparisonColumns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 70, render: (val: unknown) => <Text type="secondary">{String(val)}</Text> },
        ...fields.map((field) => ({
          title: field.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
          key: field,
          render: (_: unknown, row: Record<string, unknown>) => {
            const original = originals.find((o) => o.id === row.id);
            const oldVal = original ? original[field] : undefined;
            const newVal = row[field];
            const oldStr = formatValue(oldVal);
            const newStr = formatValue(newVal);
            if (oldStr !== newStr) {
              return (
                <Space direction="vertical" size={0}>
                  <Text delete type="danger" style={{ fontSize: 12 }}>{oldStr}</Text>
                  <Text strong style={{ color: '#52c41a' }}>{newStr}</Text>
                </Space>
              );
            }
            return <Text>{newStr}</Text>;
          },
        })),
      ];

      return (
        <Table
          dataSource={rows}
          columns={comparisonColumns}
          pagination={false}
          size="small"
          rowKey={(r) => `upd-${r.id}`}
          bordered
        />
      );
    };

    return (
      <Card
        key={update.entityId + '-entitlements'}
        style={{ marginBottom: 16 }}
        title={
          <div>
            <Tag color="purple">ENTITLEMENT CHANGES</Tag>
            <Text strong>{update.entityName}</Text>
          </div>
        }
      >
        <Alert
          message={`${totalChangesCount} entitlement change${totalChangesCount === 1 ? '' : 's'} detected`}
          description={
            <Space>
              {totalInserted > 0 && <Tag color="green">{totalInserted} inserted</Tag>}
              {totalUpdated > 0 && <Tag color="orange">{totalUpdated} updated</Tag>}
              {totalDeleted > 0 && <Tag color="red">{totalDeleted} deleted</Tag>}
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* ── Inserted Entitlements ── */}
        {(insertedEnts.length > 0 || insertedOrgEnts.length > 0) && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ color: '#389e0d' }}>
              <PlusOutlined /> Inserted
            </Title>
            {insertedEnts.length > 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Standard Entitlements</Text>
                <Table
                  dataSource={insertedEnts}
                  columns={stdColumns}
                  pagination={false}
                  size="small"
                  rowKey={(_, idx) => `ins-std-${idx}`}
                  bordered
                  style={{ marginBottom: 16 }}
                />
              </>
            )}
            {insertedOrgEnts.length > 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Organization Entitlements</Text>
                <Table
                  dataSource={insertedOrgEnts}
                  columns={orgColumns}
                  pagination={false}
                  size="small"
                  rowKey={(_, idx) => `ins-org-${idx}`}
                  bordered
                />
              </>
            )}
          </div>
        )}

        {/* ── Updated Entitlements ── */}
        {(updatedEnts.length > 0 || updatedOrgEnts.length > 0) && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ color: '#d48806' }}>
              <EditOutlined /> Updated
            </Title>
            {updatedEnts.length > 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Standard Entitlements</Text>
                {renderUpdatedTable(updatedEnts, origEnts, ['name', 'value', 'description', 'needs_quantity', 'unit'], false)}
                <div style={{ marginBottom: 16 }} />
              </>
            )}
            {updatedOrgEnts.length > 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Organization Entitlements</Text>
                {renderUpdatedTable(updatedOrgEnts, origOrgEnts, ['name', 'value', 'description', 'needs_quantity', 'unit', 'access_type'], true)}
              </>
            )}
          </div>
        )}

        {/* ── Deleted Entitlements ── */}
        {(deletedEnts.length > 0 || deletedOrgEnts.length > 0) && (
          <div style={{ marginBottom: 24 }}>
            <Title level={5} style={{ color: '#cf1322' }}>
              <MinusOutlined /> Deleted
            </Title>
            {deletedEnts.length > 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Standard Entitlements</Text>
                <Table
                  dataSource={deletedEnts}
                  columns={stdColumns}
                  pagination={false}
                  size="small"
                  rowKey={(r) => `del-std-${r.id}`}
                  bordered
                  style={{ marginBottom: 16 }}
                />
              </>
            )}
            {deletedOrgEnts.length > 0 && (
              <>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Organization Entitlements</Text>
                <Table
                  dataSource={deletedOrgEnts}
                  columns={orgColumns}
                  pagination={false}
                  size="small"
                  rowKey={(r) => `del-org-${r.id}`}
                  bordered
                />
              </>
            )}
          </div>
        )}
      </Card>
    );
  };

  const totalChanges = mainUpdates.length + relatedUpdates.length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading review data..." />
      </div>
    );
  }

  return (
    <div>
      <Title level={3}>Review Changes</Title>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Please review the changes below before submitting. You are about to update{' '}
        <Text strong>{totalChanges}</Text> {totalChanges === 1 ? 'entity' : 'entities'}.
      </Text>

      {mainUpdates.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>Main Updates</Title>
          {mainUpdates.map((update, index) => (
            <div key={`main-${update.entityId}-${index}`}>
              {renderUpdateSection(update)}
            </div>
          ))}
        </div>
      )}

      {relatedUpdates.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Title level={4}>Related Entity Updates</Title>
          {relatedUpdates.map((update, index) => (
            <div key={`related-${update.entityId}-${index}`}>
              {renderUpdateSection(update)}
            </div>
          ))}
        </div>
      )}

      {totalChanges === 0 && (
        <Card>
          <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
            No changes to review. Please go back and make some changes.
          </Text>
        </Card>
      )}
    </div>
  );
};

export default ReviewChanges;
